import { forwardRef, createElement, type HTMLAttributes } from 'react';
import Fade from '@mui/material/Fade';
import Modal from '@mui/material/Modal';
import { DialogRefContext } from './dialogRefContext';
import { removeDialog, useDialogs } from './dialogService';

/** CDK's dark backdrop (`rgba(0, 0, 0, .32)` from the vendored theme). */
const Backdrop = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(function Backdrop({ onClick }, ref) {
  return (
    <div
      ref={ref}
      className="cdk-overlay-backdrop cdk-overlay-dark-backdrop cdk-overlay-backdrop-showing"
      aria-hidden="true"
      onClick={onClick}
    />
  );
});

/**
 * Renders every open dialog. Mount once at the app root. MUI's Modal supplies the portal, focus trap, Escape and
 * backdrop handling; the markup inside is CDK's overlay + `MatDialogContainer` (overlay pane carrying `panelClass`,
 * width/height/maxWidth, surface), so Material's own dialog styles and the ported legacy rules apply unchanged.
 */
export function DialogHost() {
  const dialogs = useDialogs();

  return (
    <>
      {dialogs.map((dialog) => {
        const { options, ref, component: Content } = dialog;
        const panelClass = Array.isArray(options.panelClass) ? options.panelClass : [options.panelClass];
        const isAlignTop = panelClass.includes('dialog-align-top') || Boolean(options.position?.top);
        const topMargin = options.position?.top ?? '55px';

        return (
          <Modal
            key={dialog.id}
            open={dialog.open}
            className="cdk-overlay-container"
            closeAfterTransition
            slots={{ backdrop: Backdrop }}
            onClose={() => {
              if (!options.disableClose) ref.close(undefined);
            }}
          >
            <Fade in={dialog.open} appear timeout={150} onExited={() => removeDialog(dialog.id)}>
              <div
                className="cdk-global-overlay-wrapper"
                dir="ltr"
                style={{
                  justifyContent: 'center',
                  alignItems: isAlignTop ? 'flex-start' : 'center',
                  ...(isAlignTop ? { paddingTop: topMargin } : {}),
                }}
              >
                <div
                  className={['cdk-overlay-pane', options.width ? 'has-custom-width' : '', ...panelClass]
                    .filter(Boolean)
                    .join(' ')}
                  style={{
                    width: options.width,
                    height: options.height,
                    maxWidth: options.maxWidth ?? '80vw',
                    position: 'static',
                    ...(isAlignTop ? { alignSelf: 'flex-start' } : {}),
                  }}
                >
                  {createElement(
                    'mat-dialog-container',
                    {
                      className: 'mat-mdc-dialog-container mdc-dialog cdk-dialog-container mdc-dialog--open',
                      tabIndex: -1,
                      role: 'dialog',
                      'aria-modal': 'true',
                    },
                    <div className="mdc-dialog__container">
                      <div className="mat-mdc-dialog-surface mdc-dialog__surface">
                        <DialogRefContext.Provider value={ref as never}>
                          <Content data={(options.data ?? null) as never} dialogRef={ref as never} />
                        </DialogRefContext.Provider>
                      </div>
                    </div>,
                  )}
                </div>
              </div>
            </Fade>
          </Modal>
        );
      })}
    </>
  );
}
